import React from 'react';
import { Box, Dialog, Fade, IconButton, Typography } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { useAppSelector } from '../../../../redux/hooks';
import { useAdminLearningLifecycle } from '../Program/components/learning-lifecycle/useAdminLearningLifecycle';
import type { SetupRole } from './capabilitySetup';
import type { CapabilitySet } from './sharedSets';
import type { SharedBuildPreset } from './SharedSetView';
import AssessmentFlow from './create/AssessmentFlow';
import type { AssessmentDraft } from './create/assessmentDrafts';
import TrainingFlow, { type TrainingSeed } from './create/TrainingFlow';
import { MOBILE_SX, useIsMobileApp } from '../../../../mobile/mobileApp';
import type { TrainingRecommendation } from './create/trainingSnapshot';

export type CreationKind = 'assessment' | 'training';

const TITLE: Record<CreationKind, string> = {
  assessment: 'Build Assessment',
  training: 'Build Training'
};

interface FlowData {
  /** Every role set up in Capability Setup. */
  roles: SetupRole[];
  /** The organization's shared capabilities (Development, Standards), applied to roles. */
  sets?: CapabilitySet[];
  /** Opened from a shared set: its capabilities and the roles they apply to, chosen to start with. */
  sharedPreset?: SharedBuildPreset | null;
  /** The roles are still being read (Publish Training waits for them). */
  rolesLoading?: boolean;
  /** The attribute list the organization uses as its Teams. */
  teamsListId: string | null;
  /** An Assessment was run, for these roles. */
  onAssessmentRun?: (roleIds: string[]) => void;
  /** Run Assessment reopened on an unfinished Assessment (Continue on its card). */
  assessmentDraft?: AssessmentDraft | null;
  /** Publish Training opened from a recommended course: what it starts filled in with. */
  trainingSeed?: TrainingSeed;
  /** Publish Training reopened on a program built in it (Continue on its card). Published: only Members is left. */
  trainingResume?: { groupId: string; published?: boolean } | null;
  /** The Training's program (by id) was created, or moved on: whatever lists programs should read them again. */
  onProgramChanged?: (groupId: string) => void;
  /** Structure has been asked for: the Training is under way (with the recommendation it comes from, if any). */
  onTrainingGenerate?: (recommendation: TrainingRecommendation | null) => void;
  /** Invite & Finish: the Training is built and its people invited. */
  onTrainingFinished?: (groupId: string) => void;
}

/**
 * The flow itself, mounted only while the dialog is open: that is when it
 * loads the stages and owners its Details step offers.
 */
const FlowHost: React.FC<FlowData & { kind: CreationKind; onClose: () => void }> = ({
  kind,
  roles,
  sets,
  sharedPreset,
  rolesLoading,
  teamsListId,
  onAssessmentRun,
  assessmentDraft,
  trainingSeed,
  trainingResume,
  onProgramChanged,
  onTrainingGenerate,
  onTrainingFinished,
  onClose
}) => {
  const { pillars, orgMembers, programs, createProgramFromDrawer } = useAdminLearningLifecycle();
  // Every program name in the organization: a new one must not repeat any of them.
  const groupNames = useAppSelector((s) => s.allGroups.data?.groups);
  const existingNames = React.useMemo(
    () => [...programs.map((p) => p.name), ...(groupNames ?? []).map((g) => g.name)].filter(Boolean) as string[],
    [programs, groupNames]
  );
  const shared = {
    pillars: pillars.map((p) => ({ id: p.id, name: p.name })),
    members: orgMembers.map((m) => ({ id: m.id, name: m.name })),
    roles,
    sets,
    teamsListId,
    onClose
  };
  // From a shared set: a Training of its type, for the roles its capabilities apply to.
  const seed: TrainingSeed | undefined =
    trainingSeed ??
    (sharedPreset
      ? {
          name: '',
          stageId: null,
          audienceIds: [],
          purpose: '',
          category: sharedPreset.kind,
          roleIds: sharedPreset.roleIds,
          capabilityIds: sharedPreset.capabilityIds
        }
      : undefined);
  return kind === 'training' ? (
    <TrainingFlow
      {...shared}
      rolesLoading={rolesLoading}
      program={{ create: createProgramFromDrawer, existingNames, changed: onProgramChanged }}
      seed={seed}
      resume={trainingResume}
      onGenerate={onTrainingGenerate}
      onFinished={onTrainingFinished}
    />
  ) : (
    <AssessmentFlow {...shared} onRun={onAssessmentRun} resume={assessmentDraft} preset={sharedPreset ?? undefined} />
  );
};

/**
 * Run Assessment / Publish Training, opened over Capability Setup: closing it
 * leaves the manager where they were. It fades in at its full size, so it
 * never resizes as it opens.
 */
const CreationFlowDialog: React.FC<FlowData & { kind: CreationKind | null; onClose: () => void }> = ({
  kind,
  roles,
  sets,
  sharedPreset,
  rolesLoading,
  teamsListId,
  onAssessmentRun,
  assessmentDraft,
  trainingSeed,
  trainingResume,
  onProgramChanged,
  onTrainingGenerate,
  onTrainingFinished,
  onClose
}) => {
  // Hold the title through the fade-out, after `kind` has gone back to null.
  const [shown, setShown] = React.useState<CreationKind>('assessment');
  // A phone: the flow takes the whole screen.
  const isMobileApp = useIsMobileApp();
  React.useEffect(() => {
    if (kind) setShown(kind);
  }, [kind]);
  return (
    <Dialog
      open={Boolean(kind)}
      onClose={onClose}
      fullScreen={isMobileApp}
      maxWidth={false}
      TransitionComponent={Fade}
      transitionDuration={{ enter: 260, exit: 180 }}
      slotProps={{
        backdrop: {
          sx: {
            backgroundColor: 'rgba(0, 0, 0, 0.55)',
            backdropFilter: 'blur(6px)',
            WebkitBackdropFilter: 'blur(6px)'
          }
        }
      }}
      PaperProps={{
        sx: {
          width: '96vw',
          maxWidth: '96vw',
          height: '94vh',
          m: 2,
          bgcolor: '#0F0F0F',
          border: '1px solid #444444',
          borderRadius: '12px',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
          backgroundImage: 'none',
          [MOBILE_SX]: { width: '100%', maxWidth: '100%', height: '100%', m: 0, border: 'none', borderRadius: 0 }
        }
      }}
    >
      <Box
        sx={{
          px: '24px',
          py: '16px',
          borderBottom: '1px solid #2A2A2A',
          display: 'flex',
          alignItems: 'center',
          gap: '12px',
          flexShrink: 0,
          [MOBILE_SX]: { px: '16px', py: '6px' }
        }}
      >
        <Typography sx={{ flex: 1, color: '#FDFDFD', fontFamily: 'Inter, sans-serif', fontSize: 18, fontWeight: 700 }}>
          {TITLE[shown]}
        </Typography>
        <IconButton aria-label="Close" onClick={onClose} sx={{ color: '#B6B6B6', '&:hover': { color: '#FDFDFD' } }}>
          <CloseIcon sx={{ fontSize: 20 }} />
        </IconButton>
      </Box>
      <Box sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
        {kind && (
          <FlowHost
            kind={kind}
            roles={roles}
            sets={sets}
            sharedPreset={sharedPreset}
            rolesLoading={rolesLoading}
            teamsListId={teamsListId}
            onAssessmentRun={onAssessmentRun}
            assessmentDraft={assessmentDraft}
            trainingSeed={trainingSeed}
            trainingResume={trainingResume}
            onProgramChanged={onProgramChanged}
            onTrainingGenerate={onTrainingGenerate}
            onTrainingFinished={onTrainingFinished}
            onClose={onClose}
          />
        )}
      </Box>
    </Dialog>
  );
};

export default CreationFlowDialog;
